import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ClipboardText, DownloadSimple, PencilSimple, Plus, Trash } from 'phosphor-react';
import { IEstatutoPessoa } from '../../hooks/useEstatuto';
import { respostaVaga } from './respostaVaga';

// Ordem das colunas da planilha modelo (public/modelos/estatuto-fundadores.xlsx).
const COLUNAS: (keyof IEstatutoPessoa)[] = [
  'nome',
  'sexo',
  'nacionalidade',
  'estado_civil',
  'profissao',
  'rg_numero',
  'rg_orgao',
  'cpf',
  'endereco',
  'email',
  'cargo',
  'presente',
];

const ROTULOS: Record<string, string> = {
  nome: 'Nome completo',
  nacionalidade: 'Nacionalidade',
  estado_civil: 'Estado civil',
  profissao: 'Profissão',
  rg_numero: 'RG (número)',
  rg_orgao: 'Órgão expedidor do RG',
  cpf: 'CPF',
  endereco: 'Endereço residencial com CEP',
  email: 'E-mail',
};

const VAZIA: IEstatutoPessoa = {
  nome: '',
  sexo: '',
  nacionalidade: 'brasileira',
  estado_civil: '',
  profissao: 'estudante',
  rg_numero: '',
  rg_orgao: '',
  cpf: '',
  endereco: '',
  email: '',
  cargo: null,
  presente: true,
};

// Sugestões do campo (o texto é livre; os documentos fazem a concordância pelo sexo).
const ESTADOS_CIVIS = [
  'solteiro(a)',
  'casado(a)',
  'divorciado(a)',
  'viúvo(a)',
  'separado(a) judicialmente',
  'em união estável',
];

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const soDigitos = (v: string) => (v || '').replace(/\D/g, '');

/** "Feminino", "F", "masculino"... ("Mulher"/"Homem" ficam em branco e são apontados). */
export const sexoDoTexto = (t: string): 'F' | 'M' | '' => {
  if (/^f(em(inino)?)?$/i.test((t || '').trim())) return 'F';
  return /^m(asc(ulino)?)?$/i.test((t || '').trim()) ? 'M' : '';
};

/** CPF que a planilha virou número perde o zero à esquerda. */
export const cpfDaPlanilha = (t: string) => {
  const d = soDigitos(t);
  return d.length >= 9 && d.length < 11 ? d.padStart(11, '0') : d;
};
export const formatarCpf = (v: string) =>
  soDigitos(v).replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');

export const cpfValido = (v: string) => {
  const d = soDigitos(v);
  if (d.length !== 11 || /^(\d)\1+$/.test(d)) return false;
  return [9, 10].every(n => {
    let soma = 0;
    for (let i = 0; i < n; i += 1) soma += Number(d[i]) * (n + 1 - i);
    return ((soma * 10) % 11) % 10 === Number(d[n]);
  });
};

// "não sei", "n/a", "?" no lugar do dado: sairia escrito no documento (CPF e e-mail já têm a
// conferência deles).
const camposVagos = (p: IEstatutoPessoa) =>
  Object.keys(ROTULOS).filter(c => c !== 'cpf' && c !== 'email' && respostaVaga((p as any)[c]));

/** O que falta ou está errado numa pessoa (vazio = ok). */
export const problemasDaPessoa = (p: IEstatutoPessoa): string[] => {
  const faltando = Object.keys(ROTULOS).filter(c => !String((p as any)[c] ?? '').trim());
  const out = faltando.map(c => `${ROTULOS[c]} em branco`);
  if (!p.sexo) out.push('Sexo em branco');
  const vagos = camposVagos(p);
  // "?" no nome é resposta vaga, não nome curto: 1 aviso por campo.
  if (p.nome.trim() && p.nome.trim().length < 3 && !vagos.includes('nome'))
    out.push('Nome muito curto');
  if (p.endereco.trim() && p.endereco.trim().length < 5 && !vagos.includes('endereco'))
    out.push('Endereço muito curto');
  if (p.cpf && !cpfValido(p.cpf)) out.push('CPF inválido');
  if (p.email && !EMAIL_REGEX.test(p.email.trim())) out.push('E-mail inválido');
  vagos.forEach(c => out.push(`${ROTULOS[c]}: informe o dado`));
  return out;
};

/** Problemas da lista inteira (o back confere de novo ao enviar). */
export const problemasDaLista = (pessoas: IEstatutoPessoa[]): string | null => {
  if (!pessoas.length) return 'Inclua os fundadores.';
  const incompletas = pessoas.filter(p => problemasDaPessoa(p).length).length;
  if (incompletas) return `${incompletas} pessoa(s) com dado faltando ou inválido (veja na lista).`;
  const cpfs = pessoas.map(p => soDigitos(p.cpf));
  if (new Set(cpfs).size !== cpfs.length) return 'Há CPF repetido na lista.';
  if (pessoas.filter(p => p.cargo === 'Presidente').length !== 1)
    return 'A diretoria precisa de exatamente 1 Presidente.';
  const ausente = pessoas.find(p => p.cargo && !p.presente);
  if (ausente) return `${ausente.nome} tem cargo na diretoria e precisa estar presente.`;
  return null;
};

const normalizar = (s: string) =>
  (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');

/** "Tesoureira", "tesoureiro", "Tesoureiro(a)" -> "Tesoureiro(a)". */
const cargoDoTexto = (texto: string, cargos: string[]): string | null | undefined => {
  if (!texto.trim()) return null;
  const alvo = normalizar(texto);
  return cargos.find(c =>
    [c, c.replace(/\(a\)/g, ''), c.replace(/o\(a\)/g, 'a').replace(/\(a\)/g, 'a')]
      .map(normalizar)
      .includes(alvo),
  );
};

/**
 * Células coladas do Excel/Google Planilhas: TAB entre células, quebra de linha entre linhas;
 * célula com quebra de linha (Alt+Enter), TAB ou aspas vem entre aspas, com aspas dobradas.
 */
export const lerTsv = (texto: string): string[][] => {
  const linhas: string[][] = [];
  let linha: string[] = [];
  let celula = '';
  let entreAspas = false;
  for (let i = 0; i < texto.length; i += 1) {
    const ch = texto[i];
    if (entreAspas) {
      if (ch === '"' && texto[i + 1] === '"') {
        celula += '"';
        i += 1;
      } else if (ch === '"') entreAspas = false;
      else celula += ch;
    } else if (ch === '"' && celula === '') entreAspas = true;
    else if (ch === '\t') {
      linha.push(celula);
      celula = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && texto[i + 1] === '\n') i += 1;
      linha.push(celula);
      linhas.push(linha);
      linha = [];
      celula = '';
    } else celula += ch;
  }
  linha.push(celula);
  linhas.push(linha);
  return linhas.map(l => l.map(c => c.replace(/\s*\n\s*/g, ' ').trim()));
};

/** Linhas coladas da planilha modelo (ordem de COLUNAS). */
const lerColagem = (texto: string, cargos: string[]) => {
  const avisos: string[] = [];
  const pessoas = lerTsv(texto)
    .filter(c => c.some(Boolean))
    .filter((c, i) => !(i === 0 && /^nome/i.test(c[0])))
    // A linha de exemplo da planilha modelo não entra.
    .filter(c => !/\(exemplo/i.test(c[0] ?? ''))
    .map((c, i) => {
      const p: any = { ...VAZIA };
      COLUNAS.forEach((col, j) => {
        // Célula vazia mantém o padrão (nacionalidade "brasileira", profissão "estudante").
        p[col] = (c[j] ?? '') || (col in ROTULOS ? (VAZIA as any)[col] : '');
      });
      p.sexo = sexoDoTexto(p.sexo);
      p.cpf = cpfDaPlanilha(p.cpf);
      const cargo = cargoDoTexto(p.cargo, cargos);
      if (cargo === undefined)
        avisos.push(`Linha ${i + 1}: cargo "${p.cargo}" não reconhecido (ficou sem cargo).`);
      p.cargo = cargo ?? null;
      // Presença: só "sim"/vazio conta como presente; o que não for reconhecido fica ausente
      // e é apontado (lado seguro: ausente não entra na lista de presença).
      const presenca = String(p.presente ?? '').trim();
      if (!presenca || /^(s|sim|x|presente|1|true|verdadeiro)$/i.test(presenca)) p.presente = true;
      else {
        p.presente = false;
        if (!/^(n|nao|não|no|0|ausente|falso|false)$/i.test(presenca))
          avisos.push(`Linha ${i + 1}: presença "${presenca}" não reconhecida (ficou ausente).`);
      }
      return p as IEstatutoPessoa;
    });
  return { pessoas, avisos };
};

interface PessoasFieldProps {
  value: IEstatutoPessoa[];
  onChange: (v: IEstatutoPessoa[]) => void;
  cargos: string[];
}

export const PessoasField: React.FC<PessoasFieldProps> = ({ value, onChange, cargos }) => {
  const [colando, setColando] = useState(false);
  const [colagem, setColagem] = useState('');
  // "Limpar lista" pede um 2º clique (colagem errada se desfaz sem remover 1 a 1).
  const [limpando, setLimpando] = useState(false);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [editando, setEditando] = useState<{ indice: number; pessoa: IEstatutoPessoa } | null>(
    null,
  );

  const importar = () => {
    const lido = lerColagem(colagem, cargos);
    onChange([...value, ...lido.pessoas]);
    setAvisos([`${lido.pessoas.length} pessoa(s) incluída(s).`, ...lido.avisos]);
    setColagem('');
    setColando(false);
  };

  const salvar = () => {
    if (!editando) return;
    const pessoa = { ...editando.pessoa, cpf: soDigitos(editando.pessoa.cpf) };
    onChange(
      editando.indice < 0
        ? [...value, pessoa]
        : value.map((p, i) => (i === editando.indice ? pessoa : p)),
    );
    setEditando(null);
  };

  const campo = (nome: keyof IEstatutoPessoa) => ({
    value: String((editando?.pessoa as any)?.[nome] ?? ''),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      editando &&
      setEditando({ ...editando, pessoa: { ...editando.pessoa, [nome]: e.target.value } }),
  });

  const presentes = value.filter(p => p.presente).length;
  const diretoria = value.filter(p => p.cargo).length;

  // FormControl próprio: a colagem e os botões não herdam o "obrigatório/inválido" do campo.
  return (
    <FormControl>
      <Flex gap={2} wrap="wrap" mb={3}>
        <Button
          size="sm"
          leftIcon={<Plus size={14} weight="bold" />}
          onClick={() => setEditando({ indice: -1, pessoa: { ...VAZIA } })}
        >
          Adicionar pessoa
        </Button>
        <Button
          size="sm"
          variant="outline"
          leftIcon={<ClipboardText size={14} weight="bold" />}
          onClick={() => setColando(!colando)}
        >
          Colar da planilha
        </Button>
        <Button
          as={Link}
          size="sm"
          variant="ghost"
          leftIcon={<DownloadSimple size={14} weight="bold" />}
          href="/modelos/estatuto-fundadores.xlsx"
          download
          _hover={{ textDecoration: 'none' }}
        >
          Baixar planilha modelo
        </Button>
        {value.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => {
              if (!limpando) {
                setLimpando(true);
                return;
              }
              onChange([]);
              setAvisos([]);
              setLimpando(false);
            }}
            onBlur={() => setLimpando(false)}
          >
            {limpando ? `Clique de novo para apagar as ${value.length}` : 'Limpar lista'}
          </Button>
        )}
      </Flex>

      <Collapse in={colando} animateOpacity>
        <Box bg="gray.50" borderWidth="1px" borderRadius="lg" p={3} mb={3}>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Preencha a planilha modelo (uma pessoa por linha), selecione as linhas no Excel ou no
            Google Planilhas, copie e cole aqui. Colunas, nesta ordem:{' '}
            {[
              ROTULOS.nome,
              'Sexo',
              ...Object.values(ROTULOS).slice(1),
              'Cargo na diretoria',
              'Presente (Sim/Não)',
            ].join(' · ')}
            .
          </Text>
          <Textarea
            aria-label="Colar fundadores da planilha"
            value={colagem}
            onChange={e => setColagem(e.target.value)}
            placeholder="Cole aqui as linhas copiadas da planilha"
            minH="120px"
            bg="white"
            fontSize="xs"
          />
          <Flex justify="flex-end" mt={2}>
            <Button size="sm" colorScheme="blue" onClick={importar} isDisabled={!colagem.trim()}>
              Incluir na lista
            </Button>
          </Flex>
        </Box>
      </Collapse>

      {avisos.length > 0 && (
        <Alert
          status={avisos.length > 1 ? 'warning' : 'success'}
          borderRadius="lg"
          mb={3}
          fontSize="xs"
        >
          <AlertIcon />
          <Box>
            {avisos.map(a => (
              <Text key={a}>{a}</Text>
            ))}
          </Box>
        </Alert>
      )}

      <Text fontSize="xs" color="gray.500" mb={2}>
        {value.length} fundador(es) · {presentes} presente(s) na assembleia · {diretoria} na
        diretoria
      </Text>

      {value.length > 0 && (
        <Box overflowX="auto" borderWidth="1px" borderRadius="lg" maxH="420px" overflowY="auto">
          <Table size="sm">
            <Thead position="sticky" top={0} bg="white" zIndex={1}>
              <Tr>
                <Th>Nome e CPF</Th>
                <Th>Cargo</Th>
                <Th>Presente</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {value.map((p, i) => {
                const problemas = problemasDaPessoa(p);
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <Tr key={i} bg={problemas.length ? 'red.50' : undefined}>
                    <Td>
                      <Text fontSize="xs">
                        {i + 1}. {p.nome || '—'}
                      </Text>
                      <Text fontSize="10px" color="gray.500">
                        {p.cpf ? formatarCpf(p.cpf) : 'sem CPF'}
                      </Text>
                      {problemas.length > 0 && (
                        <Text fontSize="10px" color="red.600">
                          {problemas.join(' · ')}
                        </Text>
                      )}
                    </Td>
                    <Td fontSize="xs" color="purple.700">
                      {p.cargo}
                    </Td>
                    <Td fontSize="xs">{p.presente ? 'Sim' : 'Não'}</Td>
                    <Td whiteSpace="nowrap">
                      <IconButton
                        aria-label={`Editar ${p.nome || `linha ${i + 1}`}`}
                        size="xs"
                        variant="ghost"
                        icon={<PencilSimple size={14} />}
                        onClick={() => setEditando({ indice: i, pessoa: { ...p } })}
                      />
                      <IconButton
                        aria-label={`Remover ${p.nome || `linha ${i + 1}`}`}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        icon={<Trash size={14} />}
                        onClick={() => onChange(value.filter((_, j) => j !== i))}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal
        isOpen={!!editando}
        onClose={() => setEditando(null)}
        // Clique fora não descarta o que foi digitado.
        closeOnOverlayClick={false}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">
            {editando && editando.indice >= 0 ? 'Editar pessoa' : 'Adicionar pessoa'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {Object.keys(ROTULOS).map(nome => (
                <FormControl
                  key={nome}
                  isRequired
                  gridColumn={nome === 'nome' || nome === 'endereco' ? { md: 'span 2' } : undefined}
                >
                  <FormLabel fontSize="xs" mb={1}>
                    {ROTULOS[nome]}
                  </FormLabel>
                  <Input
                    size="sm"
                    list={nome === 'estado_civil' ? 'estados-civis' : undefined}
                    {...campo(nome as keyof IEstatutoPessoa)}
                  />
                </FormControl>
              ))}
              <datalist id="estados-civis">
                {ESTADOS_CIVIS.map(e => (
                  <option key={e} value={e}>
                    {e}
                  </option>
                ))}
              </datalist>
              <FormControl isRequired>
                <FormLabel fontSize="xs" mb={1}>
                  Sexo
                </FormLabel>
                <Select
                  size="sm"
                  value={editando?.pessoa.sexo ?? ''}
                  placeholder="Selecione"
                  onChange={e =>
                    editando &&
                    setEditando({
                      ...editando,
                      pessoa: {
                        ...editando.pessoa,
                        sexo: e.target.value as IEstatutoPessoa['sexo'],
                      },
                    })
                  }
                >
                  <option value="F">Feminino</option>
                  <option value="M">Masculino</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>
                  Cargo na diretoria
                </FormLabel>
                <Select
                  size="sm"
                  value={editando?.pessoa.cargo ?? ''}
                  onChange={e =>
                    editando &&
                    setEditando({
                      ...editando,
                      pessoa: { ...editando.pessoa, cargo: e.target.value || null },
                    })
                  }
                >
                  <option value="">Sem cargo (só fundador)</option>
                  {cargos.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="center" gap={2} mt={{ md: 6 }}>
                <Switch
                  isChecked={!!editando?.pessoa.presente}
                  onChange={e =>
                    editando &&
                    setEditando({
                      ...editando,
                      pessoa: { ...editando.pessoa, presente: e.target.checked },
                    })
                  }
                />
                <FormLabel fontSize="xs" mb={0}>
                  Presente na assembleia
                </FormLabel>
              </FormControl>
            </SimpleGrid>
            {editando && problemasDaPessoa(editando.pessoa).length > 0 && (
              <Text fontSize="xs" color="red.600" mt={3}>
                {problemasDaPessoa(editando.pessoa).join(' · ')}
              </Text>
            )}
            {editando && camposVagos(editando.pessoa).length > 0 && (
              <Text fontSize="xs" color="gray.600" mt={1}>
                Se não souber, confirme com a pessoa antes de enviar.
              </Text>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={salvar}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </FormControl>
  );
};
